<section
  class="dashboard-widget bg-standardwhite border border-grey-300 rounded-md shadow-sm p-4 flex flex-col min-h-[13rem]"
  [class.dashboard-widget-executive]="isExecutiveWidget()"
  [ngClass]="[widgetStatusClass(), selected ? 'dashboard-widget-selected' : '', drilldownDisabled() ? 'dashboard-widget-static' : 'dashboard-widget-clickable']"
  [attr.role]="supportsWholeWidgetDrilldown() && !drilldownDisabled() ? 'button' : null"
  [attr.tabindex]="supportsWholeWidgetDrilldown() && !drilldownDisabled() ? 0 : null"
  [attr.aria-label]="supportsWholeWidgetDrilldown() && !drilldownDisabled() ? widgetStatusAccessibilityText() + '. Open ' + widget.Title + ' drill-down' : null"
  (keydown)="selectWidgetFromKeyboard($event)"
  (click)="selectWidget()">
  <span class="sr-only">{{widgetStatusAccessibilityText()}}.</span>
  <div class="flex items-start justify-between gap-3 mb-3">
    <div>
      <h3 class="text-green-700">{{widget.Title | customerText}}</h3>
      <p class="text-grey-900 text-sm mt-1">{{widget.Subtitle | customerText}}</p>
    </div>
    <span class="dashboard-widget-icon material-symbols-outlined text-green-300 text-3xl bg-offwhite-500 rounded-full">{{widget.Icon}}</span>
  </div>

  @if (editMode) {
    <div class="edit-toolbar">
      <button type="button" (click)="rename($event)">Update</button>
      <button type="button" (click)="configure($event)">Configure</button>
      <button type="button" (click)="remove($event)">Remove</button>
      <span class="material-symbols-outlined" title="Layout movement placeholder">open_with</span>
    </div>
  }

  @switch (widget.VisualType) {
    @case ('number') {
      <div class="flex flex-col justify-center flex-grow">
        <span [ngClass]="valueClass()">{{widgetValueDisplay() | customerText}}</span>
        @if (widget.TrendText) {
          <span class="mt-3 text-sm font-semibold" [ngClass]="trendClass()">{{percentageText(widget.TrendText)}}</span>
        }
      </div>
    }
    @case ('percentage') {
      <div class="flex flex-col justify-center flex-grow">
        <span [ngClass]="valueClass()">{{widgetValueDisplay() | customerText}}</span>
        @if (widget.TrendText) {
          <span class="mt-3 text-sm font-semibold" [ngClass]="trendClass()">{{percentageText(widget.TrendText)}}</span>
        }
      </div>
    }
    @case ('bar') {
      <div class="widget-chart">
        <p-chart [type]="chartType()" [data]="chartData()" [options]="chartOptionsFor()" (onDataSelect)="selectChartSegment($event)"></p-chart>
      </div>
    }
    @case ('line') {
      @if (isBluebirdFrequencyTrend()) {
        <div class="frequency-trend-chart" role="region" [attr.aria-label]="widget.Title + '. Values are also available in the data table below.'">
          <div class="frequency-trend-legend" aria-hidden="true">
            @for (series of frequencyTrendSeries(); track series.label) {
              <span><i [style.background-color]="series.color"></i>{{series.label | customerText}}</span>
            }
          </div>
          <svg viewBox="0 0 640 250" preserveAspectRatio="xMidYMid meet" focusable="false">
            @for (tick of frequencyTrendTicks(); track tick) {
              <line x1="52" x2="622" [attr.y1]="frequencyTrendY(tick)" [attr.y2]="frequencyTrendY(tick)" class="frequency-trend-gridline"></line>
              <text x="45" [attr.y]="frequencyTrendY(tick) + 4" text-anchor="end">{{tick}}%</text>
            }
            <line x1="52" x2="622" y1="216" y2="216" class="frequency-trend-axis"></line>
            @for (label of frequencyTrendLabels(); track $index) {
              <text [attr.x]="frequencyTrendX($index)" y="239" text-anchor="middle">{{label}}</text>
            }
            @for (series of frequencyTrendSeries(); track series.label; let seriesIndex = $index) {
              @for (segment of series.segments; track $index) {
                @if (segment.length > 1) {
                  <polyline [attr.points]="frequencyTrendPoints(segment)" [attr.stroke]="series.color" class="frequency-trend-line"></polyline>
                }
              }
              @for (point of series.points; track point.dataIndex) {
                <circle
                  [attr.cx]="frequencyTrendPointX(point, seriesIndex)"
                  [attr.cy]="frequencyTrendY(point.value)"
                  r="4"
                  [attr.fill]="series.color"
                  [attr.tabindex]="point.selection && !editMode ? 0 : null"
                  [attr.role]="point.selection && !editMode ? 'button' : null"
                  [attr.aria-label]="series.label + ', ' + frequencyTrendLabels()[point.dataIndex] + ': ' + percentageText(point.value + '%')"
                  (click)="selectFrequencyTrendPoint(point, frequencyTrendLabels()[point.dataIndex], $event)"
                  (keydown.enter)="selectFrequencyTrendPointFromKeyboard(point, frequencyTrendLabels()[point.dataIndex], $event)"
                  (keydown.space)="selectFrequencyTrendPointFromKeyboard(point, frequencyTrendLabels()[point.dataIndex], $event)"></circle>
              }
            }
          </svg>
        </div>
      } @else {
        <div class="widget-chart">
          <p-chart [type]="chartType()" [data]="chartData()" [options]="chartOptionsFor()" (onDataSelect)="selectChartSegment($event)"></p-chart>
        </div>
      }
    }
    @case ('doughnut') {
      <div class="widget-chart">
        <p-chart [type]="chartType()" [data]="chartData()" [options]="chartOptionsFor()" (onDataSelect)="selectChartSegment($event)"></p-chart>
      </div>
    }
    @case ('stackedBar') {
      <div class="widget-chart">
        <p-chart [type]="chartType()" [data]="chartData()" [options]="chartOptionsFor()" (onDataSelect)="selectChartSegment($event)"></p-chart>
      </div>
    }
    @case ('radialProgress') {
      <div
        class="radial-progress"
        [class.radial-progress-presentation]="isBluebirdRadialProgress()"
        [style.--radial-progress-value]="widget.Value"
        [attr.aria-label]="(widget.Numerator || 0) + ' of ' + (widget.Denominator || 0) + ' requirements completed'">
        <div class="widget-chart radial-progress-chart">
          <p-chart [type]="chartType()" [data]="chartData()" [options]="chartOptionsFor()" (onDataSelect)="selectChartSegment($event)"></p-chart>
        </div>
        <div class="radial-progress-value" aria-hidden="true">
          <strong>{{widgetValueDisplay() | customerText}}</strong>
          <span>{{widget.Numerator || 0}} / {{widget.Denominator || 0}}</span>
        </div>
      </div>
    }
    @case ('metricGroup') {
      <div
        class="metric-group flex-grow"
        [class.metric-group-two]="(presentationMode || isExecutiveWidget()) && widget.MetricValues?.length === 2"
        [class.metric-group-three-compact]="(presentationMode && compactMode || isExecutiveWidget()) && widget.MetricValues?.length === 3">
        @for (metric of widget.MetricValues || []; track metric.Label) {
          <button
            type="button"
            class="metric-group-item"
            [ngClass]="'metric-group-item-' + metric.Status"
            [disabled]="editMode || metric.AggregateOnly || !metric.Selection"
            [attr.aria-label]="metric.AggregateOnly ? metric.Label + ' is aggregate only' : 'Open ' + metric.Label + ' drill-down'"
            (click)="selectMetricValue(metric, $event)">
            <span class="metric-group-label">{{metric.Label | customerText}}</span>
            <strong>{{metricValueDisplay(metric)}}</strong>
            @if (metric.TrendText) {
              <span class="metric-group-trend">{{percentageText(metric.TrendText)}}</span>
            }
          </button>
        }
      </div>
    }
    @case ('table') {
      @if (isExecutiveOfficeBenchmark()) {
        <div class="office-benchmark" (click)="$event.stopPropagation()">
          <div class="office-benchmark-controls">
            <label>
              <span>Measure</span>
              <select [value]="benchmarkMeasureKey" (change)="changeBenchmarkMeasure($event)">
                @for (view of benchmarkBaseViews(); track view.MeasureKey) {
                  <option [value]="view.MeasureKey">{{view.Label}}</option>
                }
                <option value="pillar">BBC Way pillar</option>
              </select>
            </label>
            @if (benchmarkMeasureKey === 'pillar') {
              <label>
                <span>Pillar</span>
                <select [value]="benchmarkPillarKey" (change)="changeBenchmarkPillar($event)">
                  @for (view of benchmarkPillarViews(); track view.PillarKey) {
                    <option [value]="view.PillarKey">{{view.PillarName}}</option>
                  }
                </select>
              </label>
            }
          </div>

          @if (selectedBenchmarkView(); as view) {
            <p class="office-benchmark-coverage" aria-live="polite">
              {{view.NumericOfficeCount}} ranked Office{{view.NumericOfficeCount === 1 ? '' : 's'}};
              {{view.NeutralOfficeCount}} neutral. Higher values rank first.
            </p>
            <div class="overflow-x-auto">
              <table class="w-full table-auto office-benchmark-table">
                <caption class="sr-only">{{view.Label}}. Top and bottom Office rankings.</caption>
                <thead>
                  <tr>
                    <th scope="col">Group</th>
                    <th scope="col">Rank</th>
                    <th scope="col">Office</th>
                    <th scope="col">Region</th>
                    <th scope="col" class="text-right">Current</th>
                    <th scope="col" class="text-right">Movement</th>
                    <th scope="col">Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of view.TopRows; track row.OfficeID) {
                    <tr
                      [attr.tabindex]="!editMode ? 0 : null"
                      [attr.role]="!editMode ? 'button' : null"
                      [attr.aria-label]="'Open contributions for ' + row.OfficeName"
                      (click)="selectOfficeBenchmarkRow(row, $event)"
                      (keydown.enter)="selectOfficeBenchmarkRow(row, $event)"
                      (keydown.space)="selectOfficeBenchmarkRow(row, $event)">
                      <td><span class="office-benchmark-group office-benchmark-group-top">Top</span></td>
                      <td>{{row.Rank || '-'}}</td>
                      <td class="font-semibold">{{row.OfficeName}}</td>
                      <td>{{row.RegionName}}</td>
                      <td class="text-right font-semibold">{{officeBenchmarkValue(row)}}</td>
                      <td class="text-right">{{officeBenchmarkMovement(row)}}</td>
                      <td>{{row.Coverage | customerText}}</td>
                    </tr>
                  }
                  @for (row of view.BottomRows; track row.OfficeID) {
                    <tr
                      [attr.tabindex]="!editMode ? 0 : null"
                      [attr.role]="!editMode ? 'button' : null"
                      [attr.aria-label]="'Open contributions for ' + row.OfficeName"
                      (click)="selectOfficeBenchmarkRow(row, $event)"
                      (keydown.enter)="selectOfficeBenchmarkRow(row, $event)"
                      (keydown.space)="selectOfficeBenchmarkRow(row, $event)">
                      <td><span class="office-benchmark-group office-benchmark-group-bottom">Bottom</span></td>
                      <td>{{row.Rank || '-'}}</td>
                      <td class="font-semibold">{{row.OfficeName}}</td>
                      <td>{{row.RegionName}}</td>
                      <td class="text-right font-semibold">{{officeBenchmarkValue(row)}}</td>
                      <td class="text-right">{{officeBenchmarkMovement(row)}}</td>
                      <td>{{row.Coverage | customerText}}</td>
                    </tr>
                  }
                  @if (view.TopRows.length === 0 && view.BottomRows.length === 0) {
                    <tr><td colspan="7" class="office-benchmark-empty">No Offices have data for this measure.</td></tr>
                  }
                </tbody>
              </table>
            </div>
            <button
              type="button"
              class="office-benchmark-full-list"
              [disabled]="editMode || !view.FullListSelection"
              (click)="openOfficeBenchmarkRanking($event)">
              View full Office ranking
            </button>
          }
        </div>
      } @else if (isExecutiveRegionalComparisonTable()) {
        <div class="overflow-x-auto mt-2 executive-regional-comparison" (click)="$event.stopPropagation()">
          <table class="w-full table-auto executive-regional-matrix">
            <caption class="sr-only">{{widget.Title}}. BBC Way measures by Region.</caption>
            <thead>
              <tr>
                <th scope="col">Measure</th>
                @for (region of regionalComparisonRegions(); track region) {
                  <th scope="col">{{region}}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (measure of regionalComparisonMeasures(); track measure) {
                <tr>
                  <th scope="row">{{measure}}</th>
                  @for (region of regionalComparisonRegions(); track region) {
                    @if (regionalComparisonCell(region, measure); as cell) {
                      <td>
                        <button
                          type="button"
                          class="executive-regional-cell"
                          [class.executive-regional-cell-neutral]="cell.Status === 'neutral'"
                          [disabled]="editMode || !cell.Selection"
                          [attr.aria-label]="region + ', ' + measure + ': ' + percentageText(cell.Value) + '. ' + (cell.Coverage || '')"
                          (click)="selectTableRow(cell, $event)">
                          <strong>{{percentageText(cell.Value)}}</strong>
                          <span>Previous {{percentageText(cell.PreviousValue || '-')}}</span>
                          <span>Movement {{roundedTableValue(cell.Movement)}}</span>
                          <span>{{cell.Coverage | customerText}}</span>
                        </button>
                      </td>
                    } @else {
                      <td><span class="text-grey-900">-</span></td>
                    }
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else if (isBenchmarkTable()) {
        <div class="overflow-x-auto mt-2">
          <table class="w-full table-auto dashboard-benchmark-table">
            <caption class="sr-only">{{widget.Title}}. {{widget.BenchmarkTable?.AsAtLabel}}</caption>
            <thead>
              <tr>
                <th class="text-left text-sm border-b border-grey-300 pb-2">Measure</th>
                @for (scope of benchmarkScopeHeaders(); track scope.ScopeType) {
                  <th class="text-left text-sm border-b border-grey-300 pb-2">{{scope.ScopeLabel}}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of widget.BenchmarkTable?.Rows || []; track row.MeasureKey) {
                <tr>
                  <th scope="row" class="py-2 border-b border-grey-300 align-top text-left">
                    <span>{{row.Label | customerText}}</span>
                    <span class="block text-xs text-grey-900">{{row.DateLabel}}</span>
                  </th>
                  @for (cell of row.Cells; track cell.ScopeType) {
                    <td class="py-2 border-b border-grey-300 align-top">
                      <button
                        type="button"
                        class="benchmark-cell"
                        [ngClass]="'benchmark-cell-' + cell.State"
                        [disabled]="editMode || cell.AggregateOnly || cell.IsSuppressed || !cell.Selection"
                        [attr.aria-label]="row.Label + ', ' + cell.ScopeLabel + ': ' + percentageText(cell.DisplayValue) + '. ' + cell.Coverage"
                        (click)="selectBenchmarkCell(cell, $event)">
                        <strong>{{percentageText(cell.DisplayValue)}}</strong>
                        <span>{{cell.Coverage | customerText}}</span>
                        @if (cell.Message) {
                          <span>{{cell.Message | customerText}}</span>
                        }
                      </button>
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
      <div class="overflow-x-auto mt-2">
        <table
          class="w-full table-auto"
          [class.dashboard-wide-table]="hasRequirementColumns() || hasQualityColumns() || isRepeatedFindingsTable()"
          [class.dashboard-priority-table]="isExecutivePriorityQuestionsTable()">
          @if (isExecutivePriorityQuestionsTable()) {
            <caption class="sr-only">{{widget.Title}}. Ranked questions requiring improvement.</caption>
          }
          <thead>
            <tr>
              @if (hasRequirementColumns()) {
                <th class="text-left text-sm border-b border-grey-300 pb-2">{{ 'audit' | customerTerm }}</th>
                <th class="text-left text-sm border-b border-grey-300 pb-2">Frequency</th>
                <th class="text-left text-sm border-b border-grey-300 pb-2">Period</th>
                <th class="text-left text-sm border-b border-grey-300 pb-2">Completed</th>
                <th class="text-right text-sm border-b border-grey-300 pb-2">State</th>
              } @else if (hasQualityColumns()) {
                <th class="text-left text-sm border-b border-grey-300 pb-2">Rank</th>
                <th class="text-left text-sm border-b border-grey-300 pb-2">Pillar</th>
                <th class="text-right text-sm border-b border-grey-300 pb-2">Current</th>
                <th class="text-right text-sm border-b border-grey-300 pb-2">Previous</th>
                <th class="text-right text-sm border-b border-grey-300 pb-2">Movement</th>
                <th class="text-left text-sm border-b border-grey-300 pb-2">Category</th>
                <th class="text-left text-sm border-b border-grey-300 pb-2">Coverage</th>
              } @else if (isRepeatedFindingsTable()) {
                <th class="text-left text-sm border-b border-grey-300 pb-2">Finding</th>
                <th class="text-left text-sm border-b border-grey-300 pb-2">Pillar</th>
                <th class="text-right text-sm border-b border-grey-300 pb-2">Occurrences</th>
                <th class="text-left text-sm border-b border-grey-300 pb-2">Coverage</th>
              } @else if (isExecutivePriorityQuestionsTable()) {
                <th scope="col" class="text-left text-sm border-b border-grey-300 pb-2">Rank</th>
                <th scope="col" class="text-left text-sm border-b border-grey-300 pb-2">Priority question</th>
                <th scope="col" class="text-right text-sm border-b border-grey-300 pb-2">Score</th>
                <th scope="col" class="text-left text-sm border-b border-grey-300 pb-2">Coverage</th>
              } @else {
                <th class="text-left text-sm border-b border-grey-300 pb-2">Status</th>
                <th class="text-right text-sm border-b border-grey-300 pb-2">Count</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of summaryRows(); track $index) {
              <tr
                [attr.tabindex]="row.Selection && !editMode ? 0 : null"
                [attr.role]="row.Selection && !editMode ? 'button' : null"
                [attr.aria-label]="row.Selection && !editMode ? 'Open ' + row.Label + ' drill-down' : null"
                (click)="selectTableRow(row, $event)"
                (keydown.enter)="selectTableRow(row, $event)"
                (keydown.space)="selectTableRow(row, $event)">
                @if (hasRequirementColumns()) {
                  <td class="py-2 border-b border-grey-300 align-top">
                    <span class="table-status" [ngClass]="'table-status-' + row.Status" aria-hidden="true"></span>
                    <span class="sr-only">{{statusAccessibilityText(row.Status)}}.</span>
                    <span>{{row.Label | customerText}}</span>
                    <span class="block text-xs text-grey-900 ml-5">{{row.Detail | customerText}}</span>
                  </td>
                  <td class="py-2 border-b border-grey-300 align-top text-sm">{{row.Frequency || '-'}}</td>
                  <td class="py-2 border-b border-grey-300 align-top text-sm">{{row.Period || '-'}}</td>
                  <td class="py-2 border-b border-grey-300 align-top text-sm">{{row.CompletionDate || '-'}}</td>
                  <td class="py-2 border-b border-grey-300 align-top text-right font-bold">{{percentageText(row.Value)}}</td>
                } @else if (hasQualityColumns()) {
                  <td class="py-2 border-b border-grey-300 align-top">{{row.Rank || '-'}}</td>
                  <td class="py-2 border-b border-grey-300 align-top">
                    <span class="table-status" [ngClass]="'table-status-' + row.Status" aria-hidden="true"></span>
                    <span class="sr-only">{{statusAccessibilityText(row.Status)}}.</span>
                    <span>{{row.Label | customerText}}</span>
                  </td>
                  <td class="py-2 border-b border-grey-300 align-top text-right font-bold">{{percentageText(row.Value || '-')}}</td>
                  <td class="py-2 border-b border-grey-300 align-top text-right">{{percentageText(row.PreviousValue || '-')}}</td>
                  <td class="py-2 border-b border-grey-300 align-top text-right">{{roundedTableValue(row.Movement)}}</td>
                  <td class="py-2 border-b border-grey-300 align-top">{{row.Category || '-'}}</td>
                  <td class="py-2 border-b border-grey-300 align-top">{{(row.Coverage || '-') | customerText}}</td>
                } @else if (isRepeatedFindingsTable()) {
                  <td class="py-2 border-b border-grey-300 align-top">
                    <span class="table-status" [ngClass]="'table-status-' + row.Status" aria-hidden="true"></span>
                    <span class="sr-only">{{statusAccessibilityText(row.Status)}}.</span>
                    <span>{{row.Label | customerText}}</span>
                  </td>
                  <td class="py-2 border-b border-grey-300 align-top">{{(row.Detail || '-') | customerText}}</td>
                  <td class="py-2 border-b border-grey-300 align-top text-right font-bold">{{percentageText(row.Value)}}</td>
                  <td class="py-2 border-b border-grey-300 align-top">{{(row.Coverage || '-') | customerText}}</td>
                } @else if (isExecutivePriorityQuestionsTable()) {
                  <td class="py-2 border-b border-grey-300 align-top font-bold">{{row.Rank || '-'}}</td>
                  <td class="py-2 border-b border-grey-300 align-top">
                    <span class="table-status" [ngClass]="'table-status-' + row.Status" aria-hidden="true"></span>
                    <span class="sr-only">{{statusAccessibilityText(row.Status)}}.</span>
                    <span>{{row.Label | customerText}}</span>
                    <span class="block text-xs text-grey-900 ml-5">{{row.Category | customerText}} | {{row.Detail | customerText}}</span>
                  </td>
                  <td class="py-2 border-b border-grey-300 align-top text-right font-bold">{{percentageText(row.Value)}}</td>
                  <td class="py-2 border-b border-grey-300 align-top text-sm">{{(row.Coverage || '-') | customerText}}</td>
                } @else {
                  <td class="py-2 border-b border-grey-300 align-top">
                    <span class="table-status" [ngClass]="'table-status-' + row.Status" aria-hidden="true"></span>
                    <span class="sr-only">{{statusAccessibilityText(row.Status)}}.</span>
                    <span>{{row.Label | customerText}}</span>
                    <span class="block text-xs text-grey-900 ml-5">{{row.Detail | customerText}}</span>
                  </td>
                  <td class="py-2 border-b border-grey-300 align-top text-right font-bold">{{percentageText(row.Value)}}</td>
                }
              </tr>
            }
            @if (isExecutivePriorityQuestionsTable() && summaryRows().length === 0) {
              <tr class="dashboard-priority-empty-row">
                <td colspan="4">{{priorityQuestionsEmptyMessage()}}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      @if (hasSummaryPaging()) {
        <nav
          class="summary-pagination"
          [attr.aria-label]="widget.Title + ' table pages'"
          (click)="$event.stopPropagation()">
          <button
            type="button"
            class="summary-pagination-button"
            [disabled]="summaryPagingLoading || summaryPageNumber <= 1"
            [attr.aria-label]="'Previous page of ' + widget.Title"
            (click)="changeSummaryPage(summaryPageNumber - 1, $event)">Previous</button>
          <span class="summary-pagination-status" aria-live="polite">
            Page {{summaryPageNumber}} of {{summaryTotalPages()}} · rows {{summaryFirstRow()}}–{{summaryLastRow()}} of {{summaryTotalRows()}}
          </span>
          <button
            type="button"
            class="summary-pagination-button"
            [disabled]="summaryPagingLoading || summaryPageNumber >= summaryTotalPages()"
            [attr.aria-label]="'Next page of ' + widget.Title"
            (click)="changeSummaryPage(summaryPageNumber + 1, $event)">Next</button>
        </nav>
      }
      }
    }
  }

  @if (isChartWidget() && widget.VisualType !== 'radialProgress' && chartDataRows().length > 0) {
    <details class="chart-data-alternative mt-3" (click)="$event.stopPropagation()" (toggle)="chartDataToggle($event)">
      <summary>View chart data as a table</summary>
      <div class="overflow-x-auto mt-2">
        <table class="w-full table-auto">
          <caption class="sr-only">Data represented by the {{widget.Title}} chart</caption>
          <thead>
            <tr>
              <th scope="col" class="text-left text-sm border-b border-grey-300 pb-2">Series</th>
              <th scope="col" class="text-left text-sm border-b border-grey-300 pb-2">Period</th>
              <th scope="col" class="text-right text-sm border-b border-grey-300 pb-2">Value</th>
              <th scope="col" class="text-right text-sm border-b border-grey-300 pb-2">Details</th>
            </tr>
          </thead>
          <tbody>
            @for (row of chartDataRows(); track row.datasetIndex + '-' + row.dataIndex) {
              <tr>
                <td class="py-2 border-b border-grey-300">{{row.series}}</td>
                <td class="py-2 border-b border-grey-300">{{row.period}}</td>
                <td class="py-2 border-b border-grey-300 text-right">{{chartDataValue(row)}}</td>
                <td class="py-2 border-b border-grey-300 text-right">
                  @if (row.selection && !editMode) {
                    <button
                      type="button"
                      class="chart-data-action"
                      (click)="selectChartDataPoint(row, $event)"
                      (keydown.enter)="selectChartDataPointFromKeyboard(row, $event)"
                      (keydown.space)="selectChartDataPointFromKeyboard(row, $event)">
                      Open records
                    </button>
                  } @else {
                    <span class="text-grey-900">-</span>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </details>
  }

  @if (widget.FooterText) {
    <p class="text-xs text-grey-900 mt-auto pt-4">{{percentageText(widget.FooterText) | customerText}}</p>
  }
</section>
